import { Button, Checkbox, Drawer, Form, Modal } from "antd";
import Icon from "@mdi/react";
import { mdiChevronRight } from "@mdi/js";
import React, { useState } from "react";

export const COMPULSORY_TOUR_FIELD = "compulsory_tour_acknowledged";
export const COMPULSORY_TOUR_CARD_ID = "compulsory-tour-card";
export const COMPULSORY_TOUR_ACKNOWLEDGEMENT =
  "I acknowledge and agree to participate in the compulsory additional tour. The applicable fee is payable during the tour.";
export const COMPULSORY_TOUR_REQUIRED_MESSAGE = "Please acknowledge the compulsory additional tour to continue.";
// Returned by the booking create API when the acknowledgement is missing.
export const COMPULSORY_TOUR_ERROR_CODE = "compulsory_tour_not_acknowledged";

interface CompulsoryTourCardProps {
  lines: string[];
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
  // Shown under the checkbox, e.g. when the booking API rejects a missing acknowledgement.
  error?: string;
  // "v3" opens the activities in a bottom drawer instead of a modal.
  variant?: "default" | "v3";
}

// Checkout card for an itinerary's compulsory additional tour, shown above the Terms / Privacy consents.
const CompulsoryTourCard = ({ lines, checked, onCheckedChange, error, variant = "default" }: CompulsoryTourCardProps) => {
  const [detailsOpen, setDetailsOpen] = useState(false);

  if (lines.length === 0) return null;

  const agreeAndContinue = () => {
    onCheckedChange(true);
    setDetailsOpen(false);
  };

  const decline = () => {
    onCheckedChange(false);
    setDetailsOpen(false);
  };

  const title = (
    <span className="compulsory-tour-card_title">
      <span className="compulsory-tour-card_icon">
        <img src="/iceholidays-assets/images/icons/compulsory-tour-tag.svg" alt="" width={20} height={20} />
      </span>
      Compulsory Additional Tour
    </span>
  );

  const details = (
    <div className="compulsory-tour-details">
      <p className="compulsory-tour-details_intro">
        All travellers in this booking must join the activities below. Fees are payable during the tour and are not included in the paid amount.
      </p>
      <div className="compulsory-tour-details_lines">
        {lines.map((line, index) => <div key={index}>{line}</div>)}
      </div>
      {checked && (
        <div className="compulsory-tour-details_agreed">
          You've agreed to join these compulsory activities and pay the fees during the tour.
        </div>
      )}
    </div>
  );

  const footer = checked
    ? [<Button key="decline" block={variant === "v3"} className="compulsory-tour-details_decline-btn" onClick={decline}>Decline</Button>]
    : [
        variant !== "v3" && <Button key="close" onClick={() => setDetailsOpen(false)}>Close</Button>,
        <Button key="agree" type="primary" block={variant === "v3"} className="compulsory-tour-details_agree-btn" onClick={agreeAndContinue}>
          Agree and Continue
        </Button>,
      ];

  return (
    <div id={COMPULSORY_TOUR_CARD_ID} className={`compulsory-tour-card${error ? " compulsory-tour-card--error" : ""}`}>
      <div className="compulsory-tour-card_header">{title}</div>
      <div className="compulsory-tour-card_body">
        <Form.Item
          name={COMPULSORY_TOUR_FIELD}
          validateTrigger={["onSubmit"]}
          validateStatus={error ? "error" : undefined}
          help={error || undefined}
          rules={[
            {
              validator: (_, value) => {
                if (value === true || checked) return Promise.resolve();
                return Promise.reject(new Error(COMPULSORY_TOUR_REQUIRED_MESSAGE));
              },
            },
          ]}
        >
          <Checkbox checked={checked} onChange={(e) => onCheckedChange(e.target.checked)}>
            {COMPULSORY_TOUR_ACKNOWLEDGEMENT}
          </Checkbox>
        </Form.Item>
        <a className="compulsory-tour-card_link" onClick={() => setDetailsOpen(true)}>
          View activities &amp; fees <Icon path={mdiChevronRight} size="16px" />
        </a>
      </div>

      {variant === "v3" ? (
        <Drawer
          title={title}
          placement="bottom"
          height="auto"
          open={detailsOpen}
          onClose={() => setDetailsOpen(false)}
          rootClassName="v3 compulsory-tour-drawer"
          footer={footer}
        >
          {details}
        </Drawer>
      ) : (
        <Modal
          title={title}
          open={detailsOpen}
          onCancel={() => setDetailsOpen(false)}
          footer={footer}
          className="compulsory-tour-modal"
          width={600}
          centered
        >
          {details}
        </Modal>
      )}
    </div>
  );
};

export default CompulsoryTourCard;
